import {
  Command,
  User,
  UserCancelHistory,
  UserRatingLifetime,
} from '@nextforce/api'
import { Divider, Icon, makeStyles, Text } from '@rneui/themed'
import { t } from 'app/i18l'
import { font } from 'app/theme'
import React from 'react'
import { View } from 'react-native'
import { UserAvatar } from 'shared/components/user/user_avatar'
import { CommandButton } from '../command_button'
import { GoldStar } from '../gold_star'
import { ShiftTotalHoursView } from '../shift/total_hours'

interface UserDetailsProps {
  user?: User
  isInterested?: boolean
  command?: Command
  onButtonPress?(command: Command): Promise<void>
  candidateNote?: string
}

const MIN_USER_RATING = 3.5

export function UserDetails({
  user,
  isInterested,
  command,
  onButtonPress,
  candidateNote,
}: UserDetailsProps) {
  const styles = useStyles()

  if (!user?.firstName) {
    return null
  }

  const positions = user?.attributes?.positions?.join(', ')
  const workLocations = user?.attributes?.workLocations
  const certifications = user?.attributes?.certifications?.join(' | ')
  const rate = user?.attributes?.rate
  const cancelHistory = user.cancelHistory
  const downloadReminderDate = user?.downloadReminderDate

  return (
    <View>
      <View style={styles.card}>
        <View style={styles.userSelectContainer}>
          <View style={styles.userInfoContainer}>
            <UserAvatar user={user} size="medium" />
            <View style={styles.nameHoursContainer}>
              <Text
                numberOfLines={1}
                style={styles.name}
              >{`${user?.firstName} ${user?.lastName}`}</Text>
              <ShiftTotalHoursView
                hours={user?.totalScheduledHours}
                textStyle={styles.scheduledHours}
                isInterested={isInterested}
              />
              <UserRatingView ratingLifetime={user?.userRatingLifetime} />
            </View>
          </View>
          {isInterested && (
            <CommandButton
              command={command!}
              onPress={onButtonPress!}
              containerStyle={styles.selectButtonContainer}
            />
          )}
        </View>
        <Divider />
        {user?.goldStars ? (
          <View style={styles.goldStarsContainer}>
            <Text style={styles.attributesText}>{user?.goldStars}</Text>
            <GoldStar />
            <Text style={styles.attributesText}>
              {t('star', { count: user?.goldStars })}
            </Text>
          </View>
        ) : null}

        {positions ? (
          <>
            <Divider />
            <Text style={styles.attributesText}>{positions}</Text>
          </>
        ) : null}
        {workLocations ? (
          <>
            <Divider />
            <Text style={styles.attributesText}>{workLocations}</Text>
          </>
        ) : null}
        {certifications ? (
          <>
            <Divider />
            <Text style={styles.attributesText}>{certifications}</Text>
          </>
        ) : null}
        {rate ? (
          <>
            <Divider />
            <Text style={styles.attributesText}>{`Rate $${rate.toFixed(
              2
            )}`}</Text>
          </>
        ) : null}
        {downloadReminderDate ? (
          <>
            <Divider />
            <Text
              style={styles.attributesText}
            >{`Reminded to Download ${downloadReminderDate}`}</Text>
          </>
        ) : null}
        {candidateNote ? (
          <>
            <Divider />
            <Text style={[{ fontFamily: font.bold }, styles.attributesText]}>
              {t('noteToManager')}
            </Text>
            <Text style={styles.attributesText}>{candidateNote}</Text>
          </>
        ) : null}
      </View>
      {cancelHistory?.title ? (
        <CancelHistory cancelHistory={cancelHistory} />
      ) : null}
    </View>
  )
}

interface CancelHistoryProps {
  cancelHistory: UserCancelHistory
}
function CancelHistory({ cancelHistory }: CancelHistoryProps) {
  const styles = useStyles()
  return (
    <View style={styles.card}>
      <Text style={styles.cancelHistoryTitleText}>{cancelHistory.title}</Text>
      {cancelHistory.items?.map((item, index) => {
        return (
          <View key={index} style={styles.attributesText}>
            <Text>{item.shiftDetails}</Text>
            <Text>{item.reason}</Text>
          </View>
        )
      })}
    </View>
  )
}

interface UserRatingViewProps {
  ratingLifetime?: UserRatingLifetime
}

function UserRatingView({ ratingLifetime }: UserRatingViewProps) {
  const styles = useStyles()
  if (!ratingLifetime?.rating || ratingLifetime.rating < MIN_USER_RATING) {
    return null
  }
  return (
    <View style={styles.rating}>
      <Icon
        name={'star'}
        type="material-design"
        color={styles.icon.color}
        style={styles.icon}
      />
      <Text
        style={styles.ratingText}
      >{`${ratingLifetime?.rating} (${ratingLifetime?.ratingsCount} ratings)`}</Text>
    </View>
  )
}

const useStyles = makeStyles(theme => ({
  card: {
    justifyContent: 'center',
    backgroundColor: theme.colors?.white,
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 3,
    },
    shadowOpacity: 0.29,
    shadowRadius: 4.65,
    marginBottom: 17,
  },
  name: {
    fontFamily: font.bold,
    fontSize: 18,
  },
  userInfoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    margin: 10,
    flex: 1,
  },
  userSelectContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  attributesText: {
    fontSize: 16,
    justifyContent: 'center',
    padding: 10,
    alignItems: 'stretch',
  },
  cancelHistoryTitleText: {
    fontFamily: font.bold,
    fontSize: 18,
    justifyContent: 'space-between',
    padding: 10,
    alignItems: 'stretch',
  },
  nameHoursContainer: {
    marginLeft: 10,
    justifyContent: 'space-between',
    flex: 1,
  },
  scheduledHours: {
    marginTop: 5,
  },
  selectButtonContainer: { marginHorizontal: 10 },
  icon: {
    color: theme.colors.primary,
    margin: 5,
  },
  rating: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
  },
  ratingText: {
    color: theme.colors.primary,
  },
  goldStarsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
  },
}))
